/**
 * Sysadmin System Library — Tags list (PR-J).
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Search, Loader2, Edit, Trash2, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  useSysLibTags, useCreateSysLibTag, useUpdateSysLibTag, useDeleteSysLibTag,
  usePublishSysLibTag, useUnpublishSysLibTag, useBulkPublishSysLib,
  type LifecycleStatus, type SysLibTag,
} from '@/lib/use-sysadmin-library';
import { getApiErrorMessage } from '@/lib/api-error';
import {
  LibraryStatusPill, LibraryUnpublishDialog,
} from '@/components/sysadmin/library-lifecycle';

export const Route = createFileRoute('/sysadmin/library/tags')({
  component: SysLibTagsPage,
});

function SysLibTagsPage() {
  const [statusFilter, setStatusFilter] = useState<LifecycleStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<SysLibTag | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<SysLibTag | null>(null);

  const q = useSysLibTags(statusFilter === 'all' ? undefined : statusFilter);
  const filtered = useMemo(() => {
    // Bug-5 (2026-05-10): envelope flipped `data` → `items` in lockstep with server.
    const items = q.data?.items ?? [];
    if (!search.trim()) return items;
    const s = search.toLowerCase();
    return items.filter((t) => t.name.toLowerCase().includes(s));
  }, [q.data, search]);

  const publishMut = usePublishSysLibTag();
  const unpublishMut = useUnpublishSysLibTag();
  const deleteMut = useDeleteSysLibTag();
  const bulkMut = useBulkPublishSysLib();

  const allSelected = filtered.length > 0 && filtered.every((t) => selectedIds.has(t.id));

  if (q.isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search tags..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
          <SelectTrigger className="w-36 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="deprecated">Deprecated</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setShowCreate(true)} size="sm" className="gap-1.5"><Plus className="h-3.5 w-3.5" />New Tag</Button>
      </div>

      {selectedIds.size > 0 && (
        <div className="rounded-lg border bg-primary/5 border-primary/30 px-3 py-2 flex items-center gap-3">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="flex-1" />
          <Button
            size="sm"
            disabled={bulkMut.isPending}
            onClick={() =>
              bulkMut.mutate(
                { entity: 'tag', ids: Array.from(selectedIds) },
                {
                  onSuccess: (r) => { toast.success(`Published ${r.published} tags`); setSelectedIds(new Set()); },
                  onError: (e: any) => toast.error(getApiErrorMessage(e, 'Bulk publish failed')),
                },
              )
            }
          >
            Publish all
          </Button>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 px-3 py-2.5 text-left">
                <Checkbox
                  aria-label="Select all"
                  checked={allSelected}
                  onCheckedChange={() => {
                    setSelectedIds((p) => {
                      const n = new Set(p);
                      if (allSelected) for (const t of filtered) n.delete(t.id);
                      else for (const t of filtered) n.add(t.id);
                      return n;
                    });
                  }}
                />
              </th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Name</th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Color</th>
              <th className="px-3 py-2.5 text-left text-[10px] uppercase tracking-wider">Status</th>
              <th className="w-10 px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-12 text-center text-muted-foreground">
                  {q.data?.items.length === 0 ? 'No system tags yet.' : 'No tags match these filters.'}
                </td>
              </tr>
            ) : (
              filtered.map((t) => (
                <tr key={t.id} className="hover:bg-muted/30">
                  <td className="px-3 py-2.5">
                    <Checkbox
                      aria-label={`Select ${t.name}`}
                      checked={selectedIds.has(t.id)}
                      onCheckedChange={() => {
                        setSelectedIds((p) => {
                          const n = new Set(p);
                          n.has(t.id) ? n.delete(t.id) : n.add(t.id);
                          return n;
                        });
                      }}
                    />
                  </td>
                  <td className="px-3 py-2.5 font-medium">
                    <button className="text-left hover:underline" onClick={() => setEditing(t)}>{t.name}</button>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{t.color}</td>
                  <td className="px-3 py-2.5"><LibraryStatusPill status={t.status} /></td>
                  <td className="px-3 py-2.5">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Tag actions" title="More actions"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setEditing(t)} className="text-xs"><Edit className="h-3.5 w-3.5 mr-2" />Edit</DropdownMenuItem>
                        {t.status === 'draft' && <DropdownMenuItem onClick={() => publishMut.mutate({ id: t.id }, { onSuccess: () => toast.success(`Published "${t.name}"`) })} className="text-xs">Publish</DropdownMenuItem>}
                        {t.status === 'published' && <DropdownMenuItem onClick={() => setUnpublishTarget(t)} className="text-xs">Unpublish…</DropdownMenuItem>}
                        {t.status === 'deprecated' && <DropdownMenuItem onClick={() => publishMut.mutate({ id: t.id })} className="text-xs">Re-publish</DropdownMenuItem>}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => {
                          if (window.confirm(`Delete tag "${t.name}"?`))
                            deleteMut.mutate({ id: t.id }, {
                              onSuccess: () => toast.success('Deleted'),
                              onError: (e: any) => toast.error(getApiErrorMessage(e, 'Delete failed')),
                            });
                        }} className="text-xs text-destructive"><Trash2 className="h-3.5 w-3.5 mr-2" />Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TagEditorDialog open={showCreate} onOpenChange={setShowCreate} tag={null} />
      <TagEditorDialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)} tag={editing} />
      {unpublishTarget && (
        <LibraryUnpublishDialog
          open={true}
          onOpenChange={(o) => !o && setUnpublishTarget(null)}
          resourceLabel={`tag "${unpublishTarget.name}"`}
          busy={unpublishMut.isPending}
          onConfirm={async ({ reason, nextStatus }) => {
            await unpublishMut.mutateAsync({ id: unpublishTarget.id, reason, nextStatus });
            toast.success(`Unpublished — now ${nextStatus}`);
            setUnpublishTarget(null);
          }}
        />
      )}
    </div>
  );
}

function TagEditorDialog({
  open, onOpenChange, tag,
}: { open: boolean; onOpenChange: (o: boolean) => void; tag: SysLibTag | null }) {
  const isEdit = tag !== null;
  const [name, setName] = useState(tag?.name ?? '');
  const [color, setColor] = useState(tag?.color ?? 'blue');
  const [submitting, setSubmitting] = useState(false);

  useMemo(() => {
    if (open) {
      setName(tag?.name ?? '');
      setColor(tag?.color ?? 'blue');
    }
  }, [open, tag]);

  const createMut = useCreateSysLibTag();
  const updateMut = useUpdateSysLibTag(tag?.id ?? '');

  const submit = async () => {
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && tag) {
        await updateMut.mutateAsync({ name: name.trim(), color });
        toast.success('Tag updated');
      } else {
        await createMut.mutateAsync({ name: name.trim(), color });
        toast.success('Tag created (status: draft)');
      }
      onOpenChange(false);
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Save failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit System Tag' : 'New System Tag'}</DialogTitle>
          <DialogDescription>Tags help tenants categorise published system content.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2"><Label htmlFor="name">Name</Label><Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} /></div>
          <div className="space-y-2">
            <Label htmlFor="color">Color</Label>
            <Select value={color} onValueChange={setColor}>
              <SelectTrigger id="color"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['blue', 'green', 'purple', 'amber', 'red', 'gray', 'pink', 'indigo'].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting || !name.trim()}>
            {submitting && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
